import React, { Fragment, useState } from 'react';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import { Formik } from 'formik';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import RemoveIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';
import moment from 'moment';
import AddButton from 'components/buttons/Add';
import CancelButton from 'components/buttons/Cancel';
import DeleteButton from 'components/buttons/Delete';
import SaveButton from 'components/buttons/Save';
import Card from 'components/Card';
import CardActions from 'components/Card/Actions';
import CardHeader from 'components/Card/Header';
import CardText from 'components/Card/Text';
import DateField from 'components/DateField';
import Dialog from 'components/Dialog';
import SelectField from 'components/SelectField';
import LoadBlur from 'components/LoadBlur';
import RowSpacer from 'components/RowSpacer';
// import tracker, { TrackerFragments } from 'lib/tracker';
import { ListItemSecondaryAction } from '@mui/material';
import {
  ADD_USER_CHILD,
  DELETE_USER_CHILD,
} from '../../schema/apolloMutations/profileMutations';
import { GET_PROFILE_DETAIL } from '../../schema/apolloQueries/profileQueries';
import { useAudienceTrackerContext } from '../../lib/trackers/Audience';

const genders = {
  f: 'Female',
  m: 'Male',
  o: 'Other / Rather Not Say',
};
const styles = {
  confirmDeleteStyle: {
    textAlign: 'center',
  },
  card: {},
  error: {
    textAlign: 'center',
  },
  notify: {
    textAlign: 'center',
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  listAddRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
};

const Children = ({ viewer, personId, personData }) => {
  const edges = [...viewer];
  const [addUserChild] = useMutation(ADD_USER_CHILD, {
    refetchQueries: [
      GET_PROFILE_DETAIL,

    ],
  });
  const [deleteUserChild] = useMutation(DELETE_USER_CHILD, {
    refetchQueries: [
      GET_PROFILE_DETAIL,

    ],
  });
  const [child, setChild] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddChildDialog, setShowAddChildDialog] = useState(false);
  const [showDeleteChildDialog, setShowDeleteChildDialog] = useState(false);
  const [showEditChildDialog, setShowEditChildDialog] = useState(false);
  const { trackProfile } = useAudienceTrackerContext();

  const handleChildAdd = () => {
    setShowAddChildDialog(true);
  };

  const handleChildAddCancel = () => {
    setShowAddChildDialog(false);
  };
  const handleChildDelete = childs => {
    setChild(childs);
    setShowDeleteChildDialog(true);
  };
  const handleChildDeleteCancel = () => {
    setChild(null);
    setShowDeleteChildDialog(false);
  };
  const saveRsSegmentation = (count) => {
    const { first, last, email, gender, birthdate } = personData?.personInfo;
    const { postalCode, city, stateOrProvince, street1, street2 } = personData?.addressInfo;
    trackProfile('profile', {
        first_name: first,
        last_name: last,
        gender,
        birth_date: moment(birthdate).format('YYYY-MM-DD[T]HH:mm:ss'),
        email,
        zipCode: postalCode,
        city,
        state: stateOrProvince,
        address: street1 || street2,
        kids: count,
      });
  };
  const handleChildDeleteConfirm = () => {
    deleteUserChild({ variables: { childId: child.id } })
      .then(res => {
        if (res.data.deleteUserChild) {
          saveRsSegmentation(edges.length - 1);
          setShowDeleteChildDialog(false);
          setChild(null);
          setSaving(false);
        }
      })
      .catch(() => {
        setChild(null);
        setSaving(false);
      });
  };
  const handleEditChild = childs => {
    setChild(childs);
    setShowEditChildDialog(true);
  };
  const handleEditChildCancel = () => {
    setChild(null);
    setShowEditChildDialog(false);
  };
  const handleFormSubmit = (childs, { setErrors, setSubmitting }) => {
    addUserChild({
      variables: {
        personId,
        children: [{ birthDate: childs.birthday, gender: childs.gender }],
      },
    })
      .then(res => {
        if (res.data.addUserChild) {
          saveRsSegmentation(edges.length + 1);
          setError(null);
          setShowAddChildDialog(false);
          setSubmitting(false);
        }
      })
      .catch(e => {
        setErrors(e);
        setSubmitting(false);
      });
  };
  const handleEditFormSubmit = (data, { setErrors, setSubmitting }) => {
    addUserChild({
      variables: {
        personId,
        children: [
          { birthDate: data.birthday, gender: data.gender, id: child.id },
        ],
      },
    })
      .then(res => {
        if (res.data.addUserChild) {
          saveRsSegmentation(edges.length);
          setError(null);
          setShowEditChildDialog(false);
          setSubmitting(false);
        }
      })
      .catch(e => {
        setErrors(e);
        setSubmitting(false);
      });
  };
  const renderEditChildForm = ({
    dirty,
    errors,
    handleBlur,
    isSubmitting,
    isValid,
    resetForm,
    setFieldTouched,
    setFieldValue,
    submitForm,
    values,
  }) => {
    const canSave = dirty && isValid && !isSubmitting;
    const now = new Date();
    return (
      <Card style={styles.card}>
        <LoadBlur loading={isSubmitting}>
          <CardHeader title='Edit Child' />
          <CardText noTopPadding>
            <SelectField
              disabled={isSubmitting}
              error={errors?.gender}
              floatingLabelFixed
              floatingLabelText='Gender'
              fullWidth
              name='gender'
              onBlur={e => {
                e.target.name = 'gender';
                handleBlur(e);
              }}
              onChange={(event, key, data) => {
                setFieldTouched('gender', true);
                setFieldValue('gender', data);
              }}
              placeholder='Choose a gender'
              showEmpty={!values.gender}
              options={genders}
              value={values.gender}
            />
            <DateField
              disabled={isSubmitting}
              errorText={errors?.birthday}
              floatingLabelText='Birth Date'
              fullWidth
              maxDate={now}
              onChange={(e, val) => {
                const date = moment(val, 'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');
                const formattedDate = date.format('YYYY-MM-DD');
                setFieldTouched('birthday', true);
                setFieldValue('birthday', formattedDate);
              }}
              value={new Date(values.birthday)}
            />
          </CardText>
          <CardActions>
            <CancelButton
              secondary
              onClick={() => {
                resetForm();
                handleEditChildCancel();
              }}
            />
            <RowSpacer />
            <SaveButton
              disabled={!canSave}
              primary={canSave}
              onClick={submitForm}
            />
          </CardActions>
        </LoadBlur>
      </Card>
    );
  };
  const renderAddChildForm = ({
    dirty,
    errors,
    handleBlur,
    isSubmitting,
    isValid,
    resetForm,
    setFieldTouched,
    setFieldValue,
    submitForm,
    values,
  }) => {
    const canSave = dirty && isValid && !isSubmitting;
    const now = new Date();
    return (
      <Card style={styles.card}>
        <LoadBlur loading={isSubmitting}>
          <CardHeader title='Add Child' />
          <CardText noTopPadding>
            <SelectField
              disabled={isSubmitting}
              error={errors?.gender}
              floatingLabelFixed
              floatingLabelText='Gender'
              fullWidth
              name='gender'
              onBlur={e => {
                e.target.name = 'gender';
                handleBlur(e);
              }}
              onChange={(event, key, data) => {
                setFieldTouched('gender', true);
                setFieldValue('gender', data);
              }}
              placeholder='Choose a gender'
              showEmpty={!values.gender}
              options={genders}
              value={values.gender}
            />
            <DateField
              disabled={isSubmitting}
              errorText={errors?.birthday}
              floatingLabelText='Birth Date'
              maxDate={now}
              onChange={(e, val) => {
                const date = moment(val, 'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');
                const formattedDate = date.format('YYYY-MM-DD');
                setFieldTouched('birthday', true);
                setFieldValue('birthday', formattedDate);
              }}
              value={values.birthday}
            />
          </CardText>
          <CardActions>
            <CancelButton
              disabled={isSubmitting}
              onClick={() => {
                resetForm();
                handleChildAddCancel();
              }}
              secondary
            />
            <RowSpacer />
            <SaveButton
              disabled={!canSave}
              primary={canSave}
              onClick={submitForm}
              style={styles.cardActionButton}
            />
          </CardActions>
        </LoadBlur>
      </Card>
    );
  };
  const now = moment(new Date()).format('YYYY-MM-DD');
  const validationSchema = Yup.object().shape({
    gender: Yup.string().required('Gender is required'),
    birthday: Yup.date()
      .max(now, 'Birthday cannot be in the future')
      .required('Birthday is required'),
  });
  return (
    <Fragment>
      <LoadBlur loading={saving}>
        <List style={styles.list}>
          {error && <div style={styles.error}>{error}</div>}
          {edges.length < 1 && (
            <div style={styles.notify}>
              Please click Add Child below to add your children.
            </div>
          )}
          {edges.map((currentChild, i) => (
            <div key={currentChild.id}>
              <ListItem>
                <ListItemText
                  primary={genders[currentChild.gender]}
                  secondary={format(currentChild.birthDate, 'MMMM Do YYYY')}
                />
                <ListItemSecondaryAction>
                  <div>
                    <IconButton
                      onClick={() => handleEditChild(currentChild)}
                      size='large'
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleChildDelete(currentChild)}
                      size='large'
                    >
                      <RemoveIcon />
                    </IconButton>
                  </div>
                </ListItemSecondaryAction>
              </ListItem>
              {i + 1 < edges.length && <Divider />}
            </div>
          ))}
        </List>
        <CardActions style={styles.cardActions}>
          <RowSpacer />
          <AddButton forceLabel label='Add Child' onClick={handleChildAdd} />
        </CardActions>
      </LoadBlur>
      <Dialog open={showAddChildDialog} modal>
        <Formik
          initialValues={{
            gender: '',
            birthday: now,
          }}
          onSubmit={handleFormSubmit}
          validationSchema={validationSchema}
        >
          {renderAddChildForm}
        </Formik>
      </Dialog>
      <Dialog open={showEditChildDialog} modal>
        <Formik
          initialValues={{
            gender: child && child.gender ? child.gender : '',
            birthday: child && child.birthday ? parse(child.birthday) : now,
          }}
          onSubmit={handleEditFormSubmit}
          validationSchema={validationSchema}
        >
          {renderEditChildForm}
        </Formik>
      </Dialog>
      <Dialog open={showDeleteChildDialog} modal>
        <Card style={styles.card}>
          <CardHeader title='Confirm Removal' />
          <CardText noTopPadding>
            Are you sure you want to remove this child?
          </CardText>
          <CardActions noTopPadding>
            <CancelButton
              onClick={handleChildDeleteCancel}
              secondary
              style={styles.cardActionButton}
            />
            <RowSpacer />
            <DeleteButton
              onClick={handleChildDeleteConfirm}
              primary
              style={styles.cardActionButton}
            />
          </CardActions>
        </Card>
      </Dialog>
    </Fragment>
  );
};
Children.propTypes = {
  viewer: PropTypes.object.isRequired,
  personData: PropTypes.object.isRequired,
  personId: PropTypes.object.isRequired,
};
export default Children;
